<div class="flex h-full min-w-[400px] flex-col overflow-y-auto rounded-3xl bg-white p-4 shadow-md">
  <div class="mb-4 flex items-center justify-start gap-4">
    <button
      class="flex items-center justify-center rounded-full bg-transparent p-2 hover:bg-gray-4"
      (click)="hideDetails()"
    >
      <mat-icon
        class="rotate-180"
        [fontIcon]="'keyboard_tab_rtl'"
      ></mat-icon>
    </button>
    <h3>{{ "medicalDocuments.labels.details" | translate }}</h3>
  </div>

  <form
    class="flex h-full flex-col items-start gap-2"
    [formGroup]="documentDetailsForm"
    (submit)="submitDetailsForm()"
  >
    @if (!isDocumentBoundToUser) {
      <div class="flex w-full items-center gap-4 rounded-lg bg-warning-100 p-4 text-sm text-black">
        <mat-icon
          class="text-orange-warning"
          [fontIcon]="'warning_amber'"
        ></mat-icon>
        <div class="flex flex-col items-start gap-1">
          <span class="font-bold">{{ "medicalDocuments.errors.analyzingErrorTitle" | translate }}</span>
          <span>{{ "medicalDocuments.errors.userIdMismatch" | translate }}</span>
        </div>
      </div>
    }

    <mat-form-field
      class="w-full"
      appearance="fill"
    >
      <mat-label>{{ "medicalDocuments.details.labels.title" | translate }}</mat-label>
      <input
        matInput
        [formControl]="fc.documentName"
      />
      @if (fc.documentName.hasError("required")) {
        <mat-error>{{ "validation.error.required" | translate }}</mat-error>
      }
    </mat-form-field>
    <shared-info-badge
      class="-mt-3"
      [withIcon]="true"
      [iconType]="'mat-icon'"
      [icon]="'attach_file'"
      [text]="formattedFileName()"
    ></shared-info-badge>

    <mat-form-field
      class="w-full"
      appearance="fill"
    >
      <mat-label>{{ "medicalDocuments.details.labels.type" | translate }}</mat-label>
      <mat-select [formControl]="fc.type">
        @for (option of documentTypes; track option) {
          <mat-option [value]="option">
            {{ "medicalDocuments.types." + option | translate }}
          </mat-option>
        }
      </mat-select>
      @if (fc.type.hasError("required")) {
        <mat-error>{{ "validation.error.required" | translate }}</mat-error>
      }
    </mat-form-field>

    <mat-form-field
      class="w-full"
      appearance="fill"
    >
      <mat-label>{{ "medicalDocuments.details.labels.date" | translate }}</mat-label>
      <input
        matInput
        [formControl]="fc.documentDate"
        [matDatepicker]="documentDatePicker"
        (click)="documentDatePicker.open()"
      />
      <mat-datepicker-toggle
        matIconSuffix
        [for]="documentDatePicker"
      >
        <mat-icon matDatepickerToggleIcon>calendar_today</mat-icon>
      </mat-datepicker-toggle>
      <mat-datepicker
        #documentDatePicker
        [touchUi]="false"
      ></mat-datepicker>
      @if (fc.documentDate.hasError("required")) {
        <mat-error>{{ "validation.error.required" | translate }}</mat-error>
      }
    </mat-form-field>

    <mat-form-field
      class="w-full"
      appearance="fill"
    >
      <mat-label>{{ "medicalDocuments.details.labels.appointment" | translate }}</mat-label>
      <mat-select [formControl]="fc.appointment">
        <mat-select-trigger>
          @if (fc.appointment.value) {
            <span class="max-w-[350px] overflow-hidden text-ellipsis font-semibold">{{ fc.appointment.value.metadata.name }}</span>
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-2">{{ formatDate(fc.appointment.value.metadata.appointment_full_date) }}</span>
              <shared-status
                [statusValue]="fc.appointment.value.status"
                [statusMessage]="'statuses.appointments.' + fc.appointment.value.status | translate"
              ></shared-status>
            </div>
          }
        </mat-select-trigger>
        @for (appointment of userAppointments(); track $index) {
          <mat-option [value]="appointment">
            <div class="flex flex-col items-start gap-0.5 py-1">
              <span>{{ appointment.metadata.name }}</span>
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-2">{{ formatDate(appointment.metadata.appointment_full_date) }}</span>
                <shared-status
                  [statusValue]="appointment.status"
                  [statusMessage]="'statuses.appointments.' + appointment.status | translate"
                ></shared-status>
              </div>
            </div>
          </mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field
      class="w-full"
      appearance="fill"
    >
      <mat-label>{{ "medicalDocuments.details.labels.tags" | translate }}</mat-label>
      <mat-select
        [formControl]="fc.documentTags"
        [multiple]="true"
      >
        <mat-select-trigger>
          <div class="flex flex-1 flex-wrap items-center justify-start gap-1 py-1">
            @if (fc.documentTags.value) {
              @for (tag of fc.documentTags.value; track $index) {
                <span class="inline-flex w-fit rounded-full border border-solid border-gray-1 px-2 py-0.5 text-xs font-bold">
                  {{ tag }}
                  <mat-icon
                    class="mb-1 !h-3 !w-3 text-center !text-xs"
                    [fontIcon]="'close'"
                    (click)="removeTag(tag, $event)"
                  ></mat-icon>
                </span>
              }
            }
          </div>
        </mat-select-trigger>
        @for (tag of healthManagerTags(); track tag.id) {
          <mat-option [value]="tag.name">
            {{ tag.name }}
          </mat-option>
        }
      </mat-select>
      @if (fc.documentTags.hasError("required")) {
        <mat-error>{{ "validation.error.required" | translate }}</mat-error>
      }
    </mat-form-field>

    @if (!isDocumentBoundToUser) {
      <div class="flex flex-col items-start gap-2 text-sm">
        <mat-checkbox [formControl]="fc.belongsToUser">
          {{ "medicalDocuments.details.labels.belongsToUser" | translate }}
        </mat-checkbox>
        @if (!fc.belongsToUser.value) {
          <span class="rounded-lg bg-red-light p-3 font-thin text-red-error-dark">
            {{ "medicalDocuments.errors.ownershipConfirmation" | translate }}
          </span>
        }
      </div>
    }

    <div class="mt-auto flex items-center gap-2 self-end">
      <button
        class="btn font-thin text-gray-2 underline hover:bg-gray-4"
        [type]="'button'"
        (click)="cancelForm()"
      >
        {{ "buttons.cancel" | translate }}
      </button>
      <button
        class="btn btn-primary"
        [type]="'submit'"
        [disabled]="documentDetailsForm.invalid"
      >
        {{ "buttons.save" | translate }}
      </button>
    </div>
  </form>
</div>
